import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Copy, CheckCircle2, Trash2 } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import PaywallPopup from './PaywallPopup';

// LocalStorage key for persisting AdCopy
const ADCOPY_STORAGE_KEY = 'finads_adcopy_data';

const AdCopyGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('realistic');
  const [useEmojis, setUseEmojis] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  // Load persisted data on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(ADCOPY_STORAGE_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.prompt) setPrompt(parsed.prompt);
        if (parsed.style) setStyle(parsed.style);
        if (parsed.useEmojis !== undefined) setUseEmojis(parsed.useEmojis);
        if (parsed.result) setResult(parsed.result);
      }
    } catch (e) {
      console.error('Error loading saved AdCopy data:', e);
    }
  }, []);

  // Save to localStorage whenever result changes
  useEffect(() => {
    if (result) {
      try {
        localStorage.setItem(ADCOPY_STORAGE_KEY, JSON.stringify({
          prompt,
          style,
          useEmojis,
          result,
          savedAt: new Date().toISOString()
        }));
      } catch (e) {
        console.error('Error saving AdCopy data:', e);
      }
    }
  }, [result, prompt, style, useEmojis]);

  // Clear saved data
  const clearSavedData = () => {
    localStorage.removeItem(ADCOPY_STORAGE_KEY);
    setResult(null);
    setPrompt('');
    toast.success('Gespeicherte Daten gelöscht');
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Bitte geben Sie ein Thema ein');
      return;
    }

    setLoading(true);
    setResult(null);
    
    try {
      const response = await axiosInstance.post('/generate/adcopy', {
        prompt,
        style,
        use_emojis: useEmojis,
        target_audience: 'financial_services'
      });
      setResult(response.data);
      toast.success('AdCopy erfolgreich generiert!');
      
      window.dispatchEvent(new CustomEvent('contentCreated', { detail: { type: 'adcopy' } }));
    } catch (error) {
      if (error.response?.status === 403) {
        setShowPaywall(true);
        return;
      }
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result?.ad_copy) {
      navigator.clipboard.writeText(result.ad_copy);
      setCopied(true);
      toast.success('In Zwischenablage kopiert!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6" data-testid="adcopy-generator">
      <Card 
        className="shadow-lg relative"
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(0, 242, 255, 0.2)'
        }}
      >
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl" style={{ color: '#F5F5F7' }}>AdCopy Generator</CardTitle>
          {result && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSavedData}
              className="hover:text-red-400"
              style={{ color: 'rgba(245, 245, 247, 0.5)' }}
              data-testid="clear-adcopy-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Neu erstellen
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="prompt" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Thema / Produkt</Label>
            <Textarea
              id="prompt"
              placeholder="z.B. Altersvorsorge, Baufinanzierung, Investmentberatung..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              data-testid="adcopy-prompt-input"
              className="resize-none"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#F5F5F7'
              }}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="style" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Stil</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger 
                  data-testid="style-select"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#F5F5F7'
                  }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#1a1f2e', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <SelectItem value="realistic" data-testid="style-realistic">Realistisch</SelectItem>
                  <SelectItem value="emotional" data-testid="style-emotional">Emotional</SelectItem>
                  <SelectItem value="professional" data-testid="style-professional">Professionell</SelectItem>
                  <SelectItem value="conversational" data-testid="style-conversational">Konversationell</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emojis" className="flex items-center justify-between" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                Emojis verwenden
                <Switch
                  id="emojis"
                  checked={useEmojis}
                  onCheckedChange={setUseEmojis}
                  data-testid="emoji-switch"
                />
              </Label>
              <p className="text-sm mt-2" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
                {useEmojis ? 'Emojis werden hinzugefügt' : 'Keine Emojis'}
              </p>
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full h-12 text-lg font-semibold"
            data-testid="generate-adcopy-button"
            style={{
              background: loading ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
              color: 'white',
              boxShadow: loading ? 'none' : '0 0 20px rgba(255, 92, 0, 0.3)'
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generiere...
              </>
            ) : (
              'AdCopy generieren'
            )}
          </Button>
        </CardContent>

        {/* AdCopy Loading Animation Overlay */}
        {loading && (
          <div 
            className="absolute inset-0 backdrop-blur-sm flex items-center justify-center rounded-lg z-50"
            style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
          >
            <style dangerouslySetInnerHTML={{__html: `
              @keyframes suckInAdCopy {
                0% {
                  opacity: 0;
                  transform: translate(var(--start-x), var(--start-y)) scale(1);
                }
                20% {
                  opacity: 1;
                }
                100% {
                  opacity: 0;
                  transform: translate(0, 0) scale(0.1);
                }
              }
              .particle-adcopy {
                animation: suckInAdCopy 3s ease-in-out infinite;
                position: absolute;
                font-size: 1.5rem;
              }
            `}} />
            
            <div className="text-center relative w-full px-8">
              {/* Animated Text Particles */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[...Array(24)].map((_, i) => {
                  const angle = (i * 360) / 24;
                  const distance = 350;
                  const x = Math.cos(angle * Math.PI / 180) * distance;
                  const y = Math.sin(angle * Math.PI / 180) * distance;
                  const icons = ['📝', '✍️', '💬', '📢', '✨', '🎯', '💡', '📱', '🚀', '⭐', '🔥', '💪'];
                  
                  return (
                    <div
                      key={i}
                      className="particle-adcopy"
                      style={{
                        '--start-x': `${x}px`,
                        '--start-y': `${y}px`,
                        animationDelay: `${i * 0.125}s`
                      }}
                    >
                      {icons[i % icons.length]}
                    </div>
                  );
                })}
              </div>

              {/* Central Vortex */}
              <div className="relative mx-auto mb-8 w-40 h-40">
                <div className="absolute inset-0 rounded-full animate-ping" style={{ animationDuration: '2s', border: '4px solid rgba(0, 242, 255, 0.3)' }}></div>
                <div className="absolute inset-4 rounded-full animate-spin" style={{ animationDuration: '3s', border: '4px solid rgba(0, 242, 255, 0.5)' }}></div>
                <div className="absolute inset-8 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse', border: '4px solid #00F2FF' }}></div>
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full p-5 shadow-2xl" style={{ background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)' }}>
                    <Copy className="w-14 h-14 text-white animate-pulse" />
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-bold mb-3 animate-pulse" style={{ color: '#F5F5F7' }}>
                ✍️ KI schreibt Ihren AdCopy
              </h3>
              <p className="text-lg mb-6" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                <span className="inline-block animate-bounce" style={{ animationDelay: '0s' }}>📝</span>
                <span className="mx-2">Kreative Texte werden generiert</span>
                <span className="inline-block animate-bounce" style={{ animationDelay: '0.2s' }}>✨</span>
              </p>
              
              <div className="max-w-md mx-auto space-y-3 text-left rounded-lg p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: '#00F2FF' }}></div>
                  <span className="font-medium" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Zielgruppe wird analysiert</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full animate-pulse" style={{ animationDelay: '0.3s', backgroundColor: '#FF5C00' }}></div>
                  <span className="font-medium" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Überzeugende Headlines erstellen</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full animate-pulse" style={{ animationDelay: '0.6s', backgroundColor: '#A78BFA' }}></div>
                  <span className="font-medium" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>Call-to-Actions optimieren</span>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>⏱️ Dauert ca. 10-20 Sekunden</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: '0s', backgroundColor: '#00F2FF' }}></div>
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: '0.2s', backgroundColor: '#FF5C00' }}></div>
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: '0.4s', backgroundColor: '#A78BFA' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {result && (
        <>
          <Card 
            className="shadow-lg" 
            data-testid="adcopy-result"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(0, 242, 255, 0.3)'
            }}
          >
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-xl" style={{ color: '#F5F5F7' }}>Generierte AdCopy</CardTitle>
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="sm"
                  data-testid="copy-adcopy-button"
                  style={{ 
                    borderColor: 'rgba(0, 242, 255, 0.3)',
                    color: '#00F2FF',
                    backgroundColor: 'rgba(0, 242, 255, 0.1)'
                  }}
                >
                  {copied ? (
                    <CheckCircle2 className="w-4 h-4" style={{ color: '#00F2FF' }} />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-6 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <pre className="whitespace-pre-wrap font-sans" style={{ color: '#F5F5F7' }} data-testid="adcopy-text">
                  {result.ad_copy}
                </pre>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="shadow-lg" 
            data-testid="adcopy-recommendation"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(167, 139, 250, 0.3)'
            }}
          >
            <CardHeader>
              <CardTitle className="text-xl" style={{ color: '#F5F5F7' }}>Empfehlung: Lead Ad vs. Landing Page</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.3)' }}>
                    <h4 className="font-bold mb-2" style={{ color: '#00F2FF' }}>✓ Lead Ad (empfohlen für Quantität)</h4>
                    <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                      <strong>Optimal zum Sammeln von Kontaktdaten</strong> direkt auf der Plattform.
                      Perfekt für die Erstansprache und schnelle Datensammlung.
                    </p>
                    <ul className="text-sm mt-2 ml-4 list-disc" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                      <li>Niedrigere Kosten pro Lead</li>
                      <li>Höhere Conversion-Rate</li>
                      <li>Ideal für Newsletter, Erstberatung</li>
                    </ul>
                  </div>

                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(167, 139, 250, 0.1)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                    <h4 className="font-bold mb-2" style={{ color: '#A78BFA' }}>✓ Landing Page (empfohlen für Qualität)</h4>
                    <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
                      <strong>Besser qualifizierte Leads</strong> durch mehrstufigen Prozess.
                      Ideal für hochwertige Finanzprodukte.
                    </p>
                    <ul className="text-sm mt-2 ml-4 list-disc" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>
                      <li>Höhere Lead-Qualität</li>
                      <li>Mehr Informationen über den Interessenten</li>
                      <li>Bessere Conversion zu Kunden</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
      
      {/* Paywall Popup */}
      <PaywallPopup 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        type="subscription" 
      />
    </div>
  );
};

export default AdCopyGenerator;
